import { isEmpty } from '@innovamat/radiance-utils';
import { Resource } from '@innovamat/resource-viewer';
import { createContext, useContext, useState } from 'react';

export type sectionType = 'Guide' | 'Session' | 'Related' | 'Assessment';
type SessionSectionProviderValue = {
  sectionActive: sectionType;
  isSectionActive: (section: sectionType) => boolean;
  changeSectionActive: (
    section: sectionType,
    onChangeSection?: (section: sectionType) => void
  ) => void;
  isGuideInResource?: boolean;
  hasLinkedResources?: boolean;
  hasAssessableResources?: boolean;
  hasResources?: boolean;
};

type SessionSectionProviderProps = {
  children: React.ReactNode;
  resource: Resource;
};

const SessionSectionContext = createContext<
  SessionSectionProviderValue | undefined
>(undefined);

export const SessionSectionProvider = ({
  children,
  resource,
}: SessionSectionProviderProps) => {
  const [sectionActive, setSectionActive] = useState<sectionType>('Guide');
  const isSectionActive = (section: sectionType) => sectionActive === section;
  const changeSectionActive = (
    section: sectionType,
    onChangeSection?: (section: sectionType) => void
  ) => {
    setSectionActive(section);
    onChangeSection?.(section);
  };

  const hasItems = (list: any) => Array.isArray(list) && list.length > 0;
  const isSession = resource && 'resources' in resource;

  const value = {
    sectionActive,
    isSectionActive,
    changeSectionActive,
    isGuideInResource: isSession && !isEmpty(resource.guide),
    hasLinkedResources:
      isSession &&
      (hasItems(resource.linkedLists) || hasItems(resource.relatedLists)),
    hasAssessableResources: isSession && hasItems(resource.assessableLists),
    hasResources: resource && 'resources' in resource,
  };

  return (
    <SessionSectionContext.Provider value={value}>
      {children}
    </SessionSectionContext.Provider>
  );
};

export const useHorizontalMenuTabs = () => {
  const context = useContext(SessionSectionContext);

  if (context === undefined) {
    throw new Error(
      'useHorizontalMenuTabs must be used within a SessionSectionContext'
    );
  }

  return context;
};
